import { clsx } from "clsx";
import { twMerge } from "tailwind-merge"

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

// Timestamps are stored as UTC ISO strings; these render them in the viewer's
// own timezone. Both return '—' for missing/unparseable values so a bad row
// never renders "Invalid Date" in a table.
export function formatDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

// Employee dates arrive either as ISO ("1990-05-14") or in the legacy
// dd.mm.yyyy shape. Parsed as a LOCAL date so a timezone offset can never
// shift the calendar day — which would break birthday matching.
function parseDateLoose(v) {
  if (!v || typeof v !== 'string') return null;
  const s = v.trim().split('T')[0];
  let y, m, d;
  if (s.includes('.')) {
    const p = s.split('.');
    if (p.length !== 3) return null;
    [d, m, y] = p.map(Number);
  } else {
    const p = s.split('-');
    if (p.length !== 3) return null;
    [y, m, d] = p.map(Number);
  }
  if (!y || !m || !d) return null;
  const dt = new Date(y, m - 1, d);
  // Reject rolled-over dates like 31 February.
  if (Number.isNaN(dt.getTime()) || dt.getFullYear() !== y
      || dt.getMonth() !== m - 1 || dt.getDate() !== d) return null;
  return dt;
}

const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;

/** Whole years and leftover months elapsed since `d`. The day-of-month check
 *  keeps a part-month from being counted early. */
function elapsedSince(d, now) {
  let months = (now.getFullYear() - d.getFullYear()) * 12 + (now.getMonth() - d.getMonth());
  if (now.getDate() < d.getDate()) months -= 1;
  if (months < 0) months = 0;
  return { years: Math.floor(months / 12), months: months % 12 };
}

/** "2 years 3 months", "1 month", "Less than a month". */
function formatSpan({ years, months }) {
  if (years === 0 && months === 0) return 'Less than a month';
  if (years === 0) return plural(months, 'month');
  if (months === 0) return plural(years, 'year');
  return `${plural(years, 'year')} ${plural(months, 'month')}`;
}

/** Whole years since `dob` as a NUMBER — for "turning 42 today".
 *  Null if missing/unparseable/in the future. */
export function ageFrom(dob) {
  const d = parseDateLoose(dob);
  if (!d) return null;
  const now = new Date();
  if (d > now) return null;
  const { years } = elapsedSince(d, now);
  if (years < 0 || years > 120) return null;
  return years;
}

/** Age to the month, e.g. "42 years 3 months" or "1 month" for an infant. */
export function fullAgeFrom(dob) {
  const d = parseDateLoose(dob);
  if (!d) return null;
  const now = new Date();
  if (d > now) return null;
  const span = elapsedSince(d, now);
  if (span.years > 120) return null;
  return formatSpan(span);
}

/** Time served, e.g. "1 year 2 months". Null if missing or in the future. */
export function tenureFrom(doj) {
  const d = parseDateLoose(doj);
  if (!d) return null;
  const now = new Date();
  if (d > now) return null;
  return formatSpan(elapsedSince(d, now));
}

/** The date a birthday falls on in a given year. A 29 Feb birthday is observed
 *  on 28 Feb in non-leap years, so those employees are never skipped. */
function birthdayInYear(year, month, day) {
  if (month === 1 && day === 29 && new Date(year, 1, 29).getDate() !== 29) {
    return new Date(year, 1, 28);
  }
  return new Date(year, month, day);
}

/** True when `dob` falls on today. */
export function isBirthdayToday(dob) {
  return daysUntilBirthday(dob) === 0;
}

/** Whole days until the next occurrence of this birthday: 0 = today,
 *  1 = tomorrow. Null when the date is missing or unparseable. */
export function daysUntilBirthday(dob) {
  const d = parseDateLoose(dob);
  if (d === null) return null;
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  let next = birthdayInYear(today.getFullYear(), d.getMonth(), d.getDate());
  // Already gone this year — roll to the next one, so late-December
  // birthdays still surface from early January.
  if (next < today) next = birthdayInYear(today.getFullYear() + 1, d.getMonth(), d.getDate());
  // Rounded because a DST shift makes the span 23 or 25 hours, not 24.
  return Math.round((next - today) / 86400000);
}

export function formatDateTime(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: true,
  });
}

// The HR financial year runs Apr -> Mar.
export const HR_MONTHS = ['Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov',
                          'Dec', 'Jan', 'Feb', 'Mar'];

export const currentFy = () => {
  const d = new Date();
  const start = d.getMonth() >= 3 ? d.getFullYear() : d.getFullYear() - 1;
  return `${start}-${String(start + 1).slice(-2)}`;
};

/**
 * Months of `fy` that have actually ENDED.
 *
 * A month still running has no closing figure, so offering it invites a
 * part-month number to be posted as if it were the month's result — and for the
 * Operations-derived KPIs its month-end tool and inventory state does not exist
 * yet at all. A past FY is wholly complete; a future one has nothing.
 */
export function completedMonths(fy) {
  const now = currentFy();
  if (fy < now) return HR_MONTHS;
  if (fy > now) return [];
  const cur = new Date().toLocaleString('en-US', { month: 'short' });
  const idx = HR_MONTHS.indexOf(cur);
  return idx < 0 ? HR_MONTHS : HR_MONTHS.slice(0, idx);
}

/** The last month that finished, and the FY it belongs to. */
export function lastCompletedPeriod() {
  const fy = currentFy();
  const done = completedMonths(fy);
  if (done.length) return { month: done[done.length - 1], fy };
  // April: nothing has finished in this FY yet, so it's the previous year's Mar.
  const start = Number(fy.split('-')[0]) - 1;
  return { month: 'Mar', fy: `${start}-${String(start + 1).slice(-2)}` };
}

// ---- the seven departments ---------------------------------------------------
// The business runs SEVEN departments — Management, Quality, Purchase, Sales,
// HR/Admin, Manufacturing, Accounts — and every other name in the master is a
// sub-unit of one of them: its row carries `parent`, the department it sits
// inside. Two levels of nesting at most (Sales > SCM > FG Store).
//
// A row may also be an ALIAS (`alias: true`): not a sub-unit but another
// spelling of its parent. QA, QC, SQA and Standard Room are Quality, and read
// "Quality" wherever a department is named. A sub-unit keeps its own name —
// PRODUCTION is drawn inside Manufacturing, it is not called Manufacturing.
//
// An employee still has exactly one department. Picking a department anywhere
// it can be picked means "this one and everything inside it".

/** A department name as it compares: trimmed, lower case, one space between
 *  words. The values are typed by hand, so "QC", "qc" and "  QC " all occur. */
const _normDept = (s) => String(s || '').trim().toLowerCase().replace(/\s+/g, ' ');

/**
 * The department master, ordered for a picker: each department followed by what
 * is inside it, nested. Every entry carries `depth` so a <select> can indent
 * children and a list can nest them.
 */
export function departmentTree(departments) {
  const all = departments || [];
  const kids = {};
  all.forEach((d) => { if (d.parent) (kids[d.parent] = kids[d.parent] || []).push(d); });
  const byName = (a, b) => (a.name || '').localeCompare(b.name || '');
  const out = [];
  const shown = new Set();
  const push = (d, depth) => {
    if (shown.has(d.id)) return;            // a cycle in the master
    shown.add(d.id);
    const children = (kids[d.name] || []).sort(byName);
    out.push({ ...d, depth, children: children.map((c) => c.name) });
    children.forEach((c) => push(c, depth + 1));
  };
  all.filter((d) => !d.parent).sort(byName).forEach((d) => push(d, 0));
  // A row whose parent is missing from the master would otherwise vanish from
  // every picker — show it at the top level rather than lose it.
  all.filter((d) => !shown.has(d.id)).sort(byName).forEach((d) => push(d, 0));
  return out;
}

/**
 * The department names a selection covers: itself plus everything inside it, at
 * any depth — picking Sales has to reach FG Store, which sits inside SCM.
 */
export function departmentMembers(departments, name) {
  if (!name) return [];
  const all = departments || [];
  const out = [name];
  const seen = new Set([_normDept(name)]);
  for (let i = 0; i < out.length; i += 1) {
    // Parent matched on the NORMALISED name, the way inDepartmentGroup below
    // already does: these names are typed by hand in two places, so the master
    // can say "Quality" where a row says "QUALITY" and they are one
    // department. The backend group walk agrees (see `_dept_children`).
    all.filter((d) => _normDept(d.parent) === _normDept(out[i])
                      && d.name && !seen.has(_normDept(d.name)))
      .forEach((d) => { seen.add(_normDept(d.name)); out.push(d.name); });
  }
  return out;
}

/** Does `value` (an employee's department) fall under the selected one? */
export function inDepartment(departments, selected, value) {
  if (!selected) return true;
  // Compared without case, so an employee recorded as "quality control"
  // belongs to Quality exactly as one recorded as "QUALITY CONTROL" does.
  return departmentMembers(departments, selected).map(_normDept)
    .includes(_normDept(value));
}

/**
 * The departments offered when CHOOSING WHICH CHART TO VIEW — the seven.
 *
 * One entry per department: a sub-unit is reachable through the department it
 * sits inside and is not listed separately. That is what makes the org
 * structure offer "Manufacturing" rather than Manufacturing plus PRODUCTION,
 * Maintenance, TOOL ROOM and MACHINE SHOP.
 *
 * Derived from the master's own parent/child data, not a list of names, so a
 * sub-unit added tomorrow collapses into its department with no code change.
 *
 * NOT for the pickers that ASSIGN a department. Those must keep offering the
 * real sub-units: an employee belongs to QC, their KPI sheet is named
 * "Quality Control · DGM", and flattening the choice would lose that.
 */
export function departmentGroups(departments) {
  return departmentTree(departments).filter((d) => !d.depth);
}

/**
 * Does `value` belong to the group the chart is filtered to?
 *
 * Two ways in, because the master is authoritative but not always complete:
 *  1. the master says so — `value` is the selection or sits inside it;
 *  2. both names DISPLAY as the same group, which catches a quality-ish
 *     department somebody typed that has not been grouped under Quality yet.
 *
 * (2) is what keeps the filter honest with the cards: a node labelled
 * "Quality" that the Quality chart excluded would be a plain contradiction.
 */
export function inDepartmentGroup(departments, selected, value) {
  if (!selected) return true;
  // Matched on the normalised name, so "qc", "  QC  " and "QC" are one
  // department — the stored values are typed by hand and all three occur.
  const members = departmentMembers(departments, selected).map(_normDept);
  if (members.includes(_normDept(value))) return true;
  // (2) applies only to a name the master has never heard of. A name it DOES
  // know is answered by the structure alone — otherwise filtering to PRODUCTION
  // would sweep in TOOL ROOM, because both roll up to Manufacturing while
  // neither is inside the other.
  const known = (departments || []).some((d) => _normDept(d?.name) === _normDept(value));
  if (known) return false;
  return topDepartment(value, departments) === topDepartment(selected, departments);
}

// ---- Quality display name ---------------------------------------------------
// Management's decision (Oct 2026): every Quality-related function reads simply
// "Quality" on the organisation charts. QA, QC, SQA, Standard Room, QUALITY
// ASSURANCE and QUALITY CONTROL are all one function to anyone looking at the
// chart, and six spellings of it made the structure harder to read, not richer.
//
// DISPLAY ONLY. The real department name is what identifies a node, filters the
// chart, navigates to its people and groups it in the Departments master — so
// the stored value is never touched and the nodes are never merged. Five
// Quality boxes stay five Quality boxes; they just share a title.
export const QUALITY_DISPLAY_NAME = 'Quality';

// Last-resort pattern, for a department HR has typed but not yet grouped under
// Quality in the master. Deliberately tight: whole-word QA/QC/SQA so a
// "HQA..." or an unrelated code cannot match, and the two multi-word names
// spelled out. The master is still the primary source — see below.
const _QUALITY_NAME_RE = /(^|\b)(qa|qc|sqa|quality|standard\s*room)(\b|$)/i;

/**
 * Walk a department up its declared parent chain and return where it lands.
 *
 * `stopAtSubUnit` decides how far: with it, the walk only climbs through ALIAS
 * rows (another spelling of the same department) and stops at the first row
 * that is a department or a sub-unit in its own right. Without it, the walk
 * goes all the way to one of the seven.
 *
 * Bounded and cycle-guarded: a parent recorded in a loop would otherwise hang
 * every screen that labels a department, which is all of them.
 */
function _climb(name, departments, stopAtSubUnit) {
  const raw = (name || '').trim();
  const rows = departments || [];
  const find = (n) => rows.find((d) => _normDept(d?.name) === _normDept(n));
  let row = find(raw);
  const seen = new Set([_normDept(raw)]);
  while (row && (row.parent || '').trim() && !(stopAtSubUnit && !row.alias)) {
    const parent = row.parent.trim();
    if (seen.has(_normDept(parent))) break;           // cycle — stop where we are
    seen.add(_normDept(parent));
    const up = find(parent);
    // A parent named but not present in the master is still the answer; it is
    // what somebody declared, and inventing a different one would be worse.
    if (!up) return parent;
    row = up;
  }
  return row ? row.name : null;
}

/**
 * What a department should be CALLED.
 *
 * A sub-unit keeps its own name: somebody in PRODUCTION reads "PRODUCTION",
 * and the department chart draws that box inside Manufacturing rather than
 * swallowing it. Only an ALIAS — another spelling of the same department, which
 * is what QA, QC, SQA and Standard Room are to Quality — reads as its parent.
 *
 * The master decides, so a spelling added under Quality tomorrow collapses with
 * no code change. The name pattern below is only a fallback for a department
 * somebody typed that is not in the master at all.
 */
export function displayDepartment(name, departments) {
  const raw = (name || '').trim();
  if (!raw) return name;
  const landed = _climb(raw, departments, true);
  if (landed) return landed;
  return _QUALITY_NAME_RE.test(raw) ? QUALITY_DISPLAY_NAME : raw;
}

/**
 * Which of the SEVEN departments something rolls up to.
 *
 * PRODUCTION rolls up to Manufacturing, FG Store through SCM to Sales, QC to
 * Quality. This is the one to count by — "how many departments are there" has
 * to answer seven, not every sub-unit and spelling the master holds.
 */
export function topDepartment(name, departments) {
  const raw = (name || '').trim();
  if (!raw) return name;
  return _climb(raw, departments, false)
    || (_QUALITY_NAME_RE.test(raw) ? QUALITY_DISPLAY_NAME : raw);
}

// A number as a person reads it, without throwing away a small one.
//
// The source-data screens used `Math.round(v * 100) / 100`, which is fine for
// 12.345 and destroys 0.003 — it renders as a flat 0, so a figure that IS
// reported reads as nothing reported. Uploaded JSON carries ratios and
// rejection rates well below 0.01, and those are exactly the values somebody
// is looking for when they open the screen.
//
// Two decimals stays the normal shape. A value smaller than that keeps enough
// decimals to survive, capped so a float artefact cannot print twenty digits.
// Trailing zeros are dropped, so 0.00300 reads as 0.003.
export const SMALL_VALUE_MAX_DP = 10;

export const fmtNum = (v) => {
  if (v === null || v === undefined || v === '') return null;
  const n = typeof v === 'number' ? v : Number(v);
  if (!Number.isFinite(n)) return String(v);
  if (Math.abs(n) >= 1000) return n.toLocaleString('en-IN', { maximumFractionDigits: 2 });
  if (n !== 0 && Math.abs(n) < 0.01) {
    // Enough places to show the first couple of significant digits.
    const dp = Math.min(SMALL_VALUE_MAX_DP, Math.ceil(-Math.log10(Math.abs(n))) + 2);
    return String(Number(n.toFixed(dp)));
  }
  return String(Number(n.toFixed(2)));
};
